import { THREE_STATE_SCORING_TABLE } from '../data/threeStateScoring.js';

export const calculateScoreChanges = (game, gameType, initiators, won, tricksWon = 0, playerStates = null, mateLosesOnFailure = false) => {
  // Handle three-state games (Piek, Misère, etc.)
  if (gameType.supportsThreeState && playerStates) {
    return calculateThreeStateScoreChanges(game, gameType, playerStates);
  }

  if (gameType.name.toLowerCase().includes('schoppen mie')) {
    const losers = initiators.length; // players who got penalties
    const schoppenMieCost = losers === 1 ? gameType.cost * 2 : gameType.cost; // 10 if 1 player, 5 if 2 players

    return game.players.map(player => {
      if (initiators.includes(player.id)) {
        return { ...player, score: player.score - schoppenMieCost };
      } else {
        const pointsToGain = (schoppenMieCost * losers) / (game.players.length - losers);
        return { ...player, score: player.score + pointsToGain };
      }
    });
  }

  let points = won ? gameType.points : gameType.cost;

  // Handle trick-based scoring
  if (gameType.supportsTricks && tricksWon !== undefined) {
    const targetTricks = gameType.baseTarget || 0;
    const trickDifference = tricksWon - targetTricks;

    if (won) {
      points += Math.max(0, trickDifference) * 5;
      // Bonus for "kapot spelen" (all 13 tricks)
      if ((gameType.name.includes('Rik') || gameType.name.includes('Troela')) && tricksWon === 13) {
        points += 35;
      }
    } else {
      points = gameType.cost + Math.abs(trickDifference) * 5;
    }
  }

  // Calculate score changes for each player
  return game.players.map(player => {
    const isInitiator = initiators.includes(player.id);

    if (won) {
      if (isInitiator) {
        return { ...player, score: player.score + points };
      } else {
        const pointsToLose = (points * initiators.length) / (4 - initiators.length);
        return { ...player, score: player.score - pointsToLose };
      }
    } else {
      if (isInitiator) {
        // Special rule for Rik games
        if (gameType.requiresMate && initiators.length === 2) {
          const isRikker = initiators[0] === player.id;
          if (isRikker) {
            if (mateLosesOnFailure) {
              // Both rikker and mate lose points
              return { ...player, score: player.score - points };
            } else {
              // Official rules: rikker loses double points, mate loses nothing
              return { ...player, score: player.score - (points * 2) };
            }
          } else {
            // This is the mate
            if (mateLosesOnFailure) {
              // Mate also loses points
              return { ...player, score: player.score - points };
            } else {
              // Official rules: mate doesn't lose points
              return player;
            }
          }
        } else {
          return { ...player, score: player.score - points };
        }
      } else {
        const pointsToGain = (points * initiators.length) / (4 - initiators.length);
        return { ...player, score: player.score + pointsToGain };
      }
    }
  });
};

export const getWinner = (players) => {
  if (!players || players.length === 0) return null;

  const maxScore = Math.max(...players.map(p => p.score));
  const playersWithMaxScore = players.filter(p => p.score === maxScore);

  // Return null if there's a tie (more than one player with max score)
  if (playersWithMaxScore.length > 1) return null;

  return playersWithMaxScore[0];
};

export const formatScore = (score) => {
  const roundedScore = Math.round(score);
  return roundedScore >= 0 ? `+${roundedScore}` : `${roundedScore}`;
};

// Determine if a game was won based on tricks achieved vs target
export const determineGameResult = (gameType, tricksWon) => {
  // For games without tricks, we can't automatically determine - would need manual input
  if (!gameType.supportsTricks || tricksWon === undefined || tricksWon === '') {
    return null; // Cannot determine automatically
  }

  const target = gameType.baseTarget || 0;
  const gameName = gameType.name.toLowerCase();

  // Special cases for certain game types
  if (gameName.includes('piek')) {
    // Piek means exactly 1 trick
    return tricksWon === 1;
  }

  if (gameName.includes('misère')) {
    // Misère means 0 tricks
    return tricksWon === 0;
  }

  if (gameName.includes('één of vijf')) {
    // "Één of vijf" means exactly 1 or 5 tricks
    return tricksWon === 1 || tricksWon === 5;
  }

  if (gameName.includes('schoppen mie')) {
    // This is more complex - player wins if they don't get the Queen of Spades or last trick
    // This would need special handling in the UI - for now return null to use manual input
    return null;
  }

  // For most trick-based games, winning means achieving the target or more
  return tricksWon >= target;
};

export const calculatePointsForRound = (gameType, tricksWon, targetTricks, won) => {
  let points = won ? gameType.points : gameType.cost;

  if (gameType.supportsTricks && tricksWon !== undefined) {
    const trickDifference = tricksWon - targetTricks;

    if (won) {
      points += Math.max(0, trickDifference) * 5;
      // Bonus for "kapot spelen" (all 13 tricks)
      if ((gameType.name.includes('Rik') || gameType.name.includes('Troela')) && tricksWon === 13) {
        points += 35;
      }
    } else {
      points = gameType.cost + Math.abs(trickDifference) * 5;
    }
  }

  return points;
};

// Calculate score changes for three-state games (Piek, Misère, etc.)
export const calculateThreeStateScoreChanges = (game, gameType, playerStates) => {
  const winners = [];
  const losers = [];
  const neutrals = [];

  // Categorize players by their state
  game.players.forEach((player, index) => {
    const state = playerStates[player.id];
    if (state === 'won') {
      winners.push(player);
    } else if (state === 'lost') {
      losers.push(player);
    } else {
      neutrals.push(player);
    }
  });

  const winnerCount = winners.length;
  const loserCount = losers.length;
  const neutralCount = neutrals.length;
  const activePlayersCount = winnerCount + loserCount; // Total people attempting the game
  const totalPlayers = game.players.length;

  // Use the imported scoring table
  const getScoreFromTable = (gameTypeName, winnerCount, activePlayersCount) => {
    const gameScores = THREE_STATE_SCORING_TABLE[gameTypeName];
    if (!gameScores || !gameScores[activePlayersCount] || !gameScores[activePlayersCount][winnerCount]) {
      // Fallback to simple calculation if not in table
      return { winner: gameType.points, loser: -gameType.points, neutral: 0 };
    }

    return gameScores[activePlayersCount][winnerCount];
  };

  const scores = getScoreFromTable(gameType.name, winnerCount, activePlayersCount);

  // Calculate score changes for each player
  return game.players.map(player => {
    const state = playerStates[player.id];
    let scoreChange = 0;

    if (state === 'won') {
      scoreChange = scores.winner || 0;
    } else if (state === 'lost') {
      scoreChange = scores.loser || 0;
    } else {
      // For neutral players, use neutral score if available, otherwise use loser score
      scoreChange = scores.neutral !== undefined ? scores.neutral : (scores.loser || 0);
    }

    return {
      ...player,
      score: player.score + scoreChange
    };
  });
};
